import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { getFullLoad, MIN_LOAD } from '../utils/loadSplit';
/**
 * Shared "order minimum" warning. Fires for standalone single-delivery
 * orders below the 3 yd order minimum (customer asked for 1–2 yd, gets
 * billed for 3).
 *
 * Multi-delivery orders no longer trigger a minimum bump on the partial
 * last load — partials are billed at their actual yardage (with the
 * small-load surcharge), so this component returns null in those cases.
 */
export function SmallLoadBreakdown({
  quantity,
  productName,
  className = ''
}: {
  quantity: number;
  productName: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!quantity || quantity <= 0) return null;
  const fullLoad = getFullLoad(productName);
  // Only fires for standalone single-delivery orders below the order min.
  if (quantity > fullLoad) return null;
  if (quantity >= MIN_LOAD) return null;
  return <div className={className} data-id="element-1569">
      <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-center gap-1.5 text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={open} data-id="element-1570">
        <Info className="w-3.5 h-3.5" data-id="element-1571" />
        You asked for {quantity} yds — billed for {MIN_LOAD} yds. Why?
      </button>
      <AnimatePresence data-id="element-1572">
        {open && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-1573">
            <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-1574">
              <p data-id="element-1575">
                <span className="font-bold" data-id="element-1576">
                  {MIN_LOAD}-yard order minimum.
                </span>{' '}
                Trucks have fixed dispatch costs, so any single delivery is
                billed at {MIN_LOAD} yds even if you only need a couple.
              </p>
              <p data-id="element-1577">
                Round up to fill the truck and you'll get more material at no
                extra cost. Need more than one truck? Partial loads in a
                multi-delivery order are billed at their actual yardage.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}